import { useRowSelect } from "@table-library/react-table-library/select";
import { Action, State } from "@table-library/react-table-library/types/common";
import { Button, DropdownMenu, TableItem } from "components/atoms";
import LabelNotificationStatus from "components/molecules/labeledStatus/LabelNotificationStatus";
import { Table, TopBar } from "components/organisms";
import { TopbarPage } from "components/templates";
import { useComponentVisible } from "hooks";
import { useTableColumnsVisibility } from "hooks/useTableColumnsVisibility";
import { usePermissionGuard } from "hooks/usePermissionGuard";
import { useHasPermission } from "hooks";
import { useStoreState } from "providers/StoreStateContext";
import { useTranslation } from "react-i18next";
import { PiCompass } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import { useGetNotificationArchiveListInfinite } from "services/notifications/index";
import {
  NOTIFICATION_CATEGORY,
  NOTIFICATION_TYPE,
} from "services/notifications/types/enums";
import { INotificationContent } from "services/notifications/types/types";
import { formatDateAndHour } from "utils/global/date.utils";
import { switchNavRoute } from "utils/global/navSwitch.utils";
import { getNodes } from "utils/table/nodes.utils";

enum SORT_COLUMNS {
  status,
  category,
  type,
  date,
  lastChanger,
}

const NotificationArchive = () => {
  usePermissionGuard('NotificationPermissionsLIST**');
  const hasPermission = useHasPermission();

  const { t } = useTranslation();
  const columnsVisibility = useTableColumnsVisibility(
    "NotificationArchive",
    SORT_COLUMNS
  );
  const navigate = useNavigate();

  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useGetNotificationArchiveListInfinite({});

  const tableData = getNodes<INotificationContent>(data?.pages.flat());

  const hasDetailPermission = hasPermission('NotificationPermissionsLIST**', 16);

  const handleNavigateClick = (item: INotificationContent): void => {
    const getCategory = switchNavRoute(item.contentCategory, item.entityId);
    navigate(getCategory);
  };

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }
    
    const selectedItem: INotificationContent | undefined = tableData.nodes.find(
      ({ id }) => id === state.id
    );

    if (selectedItem) {
      handleNavigateClick(selectedItem);
    }
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const tableSelect = hasDetailPermission ? select : undefined;

  return (
    <TopbarPage
      topbar={
        <TopBar
          path={"notifications"}
          title={t(`notification-archive.title.list`)}
          topbarContent={
            <Table.CustomColumnsButton
              columns={SORT_COLUMNS}
              isClear
              visibility={columnsVisibility}
              i18nextPrefix="input.label"
              customI18next=""
            />
          }
        ></TopBar>
      }
    >
      <Table
        id={columnsVisibility.tableId}
        select={tableSelect}
        data={tableData}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        columns={[
          {
            label: t("input.label.notificationCategory"),
            renderCell: (item: INotificationContent) => (
              <TableItem>
                <TableItem.Text
                  text={t(
                    `enum.NOTIFICATION_CATEGORY.${
                      NOTIFICATION_CATEGORY[item.contentCategory]
                    }`
                  )}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.category),
          },
          {
            label: t("input.label.notificationType"),
            renderCell: (item: INotificationContent) => (
              <TableItem>
                <TableItem.Text
                  text={t(
                    `enum.NOTIFICATION_TYPE.${
                      NOTIFICATION_TYPE[item.notificationType]
                    }`
                  )}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.type),
          },
          {
            label: t("input.label.lastChanger"),
            renderCell: (item: INotificationContent) => (
              <TableItem>
                <TableItem.Text text={`${item.firstName} ${item.lastName}`} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.lastChanger),
          },
          {
            label: t("input.label.Date"),
            renderCell: (item: INotificationContent) => (
              <TableItem>
                <TableItem.Text
                  text={formatDateAndHour(item.lastStatusChangeDate)}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.date),
          },
          {
            label: t("input.label.status"),
            renderCell: (item: INotificationContent) => (
              <TableItem>
                <LabelNotificationStatus status={item.notificationStatus} />
              </TableItem>
            ),
            hide: columnsVisibility.hide(SORT_COLUMNS.status),
          },
          {
            label: "",
            renderCell: (item: INotificationContent) => (
              <TableItem justifyPosition="end">
                <div className="flex gap-2 ">
                  <Button
                    isClear
                    onlyIcon
                    onClick={() => handleNavigateClick(item)}
                  >
                    <Button.Icon icon={<PiCompass />} />
                  </Button>
                </div>
              </TableItem>
            ),
            resize: true,
          },
        ]}
        displayMode
      />
    </TopbarPage>
  );
};

export default NotificationArchive;
